// Croqui: `components/Btn.tsx` (Btn + IconBtn).
//
// Convive com `components/ui/button.tsx` (shadcn), que o croqui não tem equivalente para
// `asChild`/`variant` e segue nas telas antigas até as ondas 27/28. Registrado em
// "Questões abertas" da WEB-50.
//
// `min-h-10` abaixo de `sm`: alvo de toque de 40px (`spec/17` §7 item 12), medido pelo
// `responsive.spec.ts`. Acima de `sm` volta à altura do croqui.

import type { MouseEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Icon, type IconName } from './icon.tsx';

export type BtnKind = 'primary' | 'card' | 'ghost' | 'danger';

const KIND: Record<BtnKind, string> = {
  primary: 'border-gold bg-gold text-bg hover:bg-gold/90',
  card: 'border-card-border bg-card text-text hover:border-tile-border hover:bg-tile',
  ghost: 'border-transparent bg-transparent text-muted hover:text-text',
  danger: 'border-danger/35 bg-transparent text-danger hover:bg-danger/10',
};

/** Botão: `primary` (dourado, um por tela), `card` (padrão), `ghost`, `danger`. */
export function Btn({
  kind = 'card',
  icon,
  children,
  small = false,
  type = 'button',
  onClick,
  disabled = false,
  className,
  ...rest
}: {
  kind?: BtnKind;
  icon?: IconName;
  children?: ReactNode;
  small?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** Recebe o evento: filtro que precisa de `stopPropagation` dentro de linha clicável. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Botão de alternância (filtro ligado/desligado). Vai direto para o `<button>`. */
  'aria-pressed'?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex min-h-10 shrink-0 items-center whitespace-nowrap border font-medium transition-colors duration-150 ease-out-quint disabled:cursor-not-allowed disabled:opacity-45',
        small
          ? 'h-[34px] gap-2 rounded-[10px] px-3 text-13 sm:min-h-0'
          : 'h-11 gap-2.5 rounded-xl px-4 text-15',
        KIND[kind],
        className,
      )}
      {...rest}
    >
      {icon !== undefined && <Icon name={icon} size={small ? 15 : 18} />}
      {children}
    </button>
  );
}

/** Botão quadrado só com ícone. `label` é obrigatório: vira `aria-label` e `title`. */
export function IconBtn({
  icon,
  label,
  onClick,
  ink = 'muted',
  disabled = false,
  className,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  ink?: 'muted' | 'danger' | 'text';
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className={cn(
        'grid size-8 min-h-10 min-w-10 shrink-0 place-items-center rounded-[10px] border border-card-border bg-card transition-colors duration-150 ease-out-quint hover:border-tile-border disabled:cursor-not-allowed disabled:opacity-45 sm:min-h-0 sm:min-w-0',
        ink === 'danger' ? 'text-danger' : ink === 'text' ? 'text-text' : 'text-muted',
        className,
      )}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}
